// Wrappers de Administración · Equipo y accesos (plan 2026-10-30 §2.3,
// T-jre9) sobre lib/api/client request(), al estilo de contratistas/api.ts.
// Lo que ya existe en lib/api/queries.ts no se duplica: el roster
// (useRoster), la organización (useMyOrganization), las tiendas asignables
// (useAssignableAssets), la afiliación de marca (useBrandAffiliations /
// useSetBrandAffiliation) y el alta de invitaciones
// (useCreateTeamInvitation) se importan de ahí. Aquí van los endpoints
// que esta pantalla estrena en el dashboard: dos lecturas y cuatro
// escrituras (tres en auth-svc y tres en core-api) más las tres rutas de
// invitar en lote (T-b7bh: plantilla, preview y commit, en core-api).

import {
  invitationBatchCommitResponseSchema,
  invitationBatchPreviewResponseSchema,
  memberScopeUpdateResponseSchema,
  memberScopesResponseSchema,
  orgInvitationListResponseSchema,
  resendInvitationResponseSchema,
  type MemberScopeUpdateBody,
  type MemberScopeUpdateResponse,
  type MemberScopesResponse,
  type OrgInvitationListResponse,
  type PatchMembershipRequest,
  type PatchMembershipResponse,
  type InvitationBatchCommitRequest,
  type InvitationBatchCommitResponse,
  type InvitationBatchPreviewResponse,
  type ResendInvitationResponse,
} from "@bitacorapro/api-types";
import { request, requestDownload, type BlobDownload } from "../../../lib/api/client";

export const equipoApi = {
  // GET /auth/invitations (auth-svc) — sólo las pendientes de la org
  // (ni aceptadas ni vencidas; una revocada vence en el acto), sin el
  // token. Cualquier miembro autenticado la lee.
  invitaciones: async (): Promise<OrgInvitationListResponse> => {
    const json = await request<unknown>("GET", "auth", "/auth/invitations");
    return orgInvitationListResponseSchema.parse(json);
  },
  // PATCH /auth/memberships/:membershipId (auth-svc) — desactivar
  // ({disabledAt: ISO}) o reactivar ({disabledAt: null}). El :id es
  // `OrgMember.membershipId` (auth_memberships.id), no el userId. Lo
  // acepta quien administra la org según la receta; 403
  // SELF_DEACTIVATION_FORBIDDEN / OWNER_DEACTIVATION_FORBIDDEN /
  // ADMIN_REQUIRED (sobre otro administrador) las esconde la pantalla.
  patchMembership: (
    membershipId: string,
    body: PatchMembershipRequest
  ): Promise<PatchMembershipResponse> =>
    request<PatchMembershipResponse>(
      "PATCH",
      "auth",
      `/auth/memberships/${encodeURIComponent(membershipId)}`,
      { body }
    ),
  // POST /auth/invitations/:id/revoke (auth-svc) — 204. Sólo pendientes;
  // cualquier otro estado es 404 INVITATION_NOT_FOUND.
  revokeInvitation: (id: string): Promise<null> =>
    request<null>("POST", "auth", `/auth/invitations/${encodeURIComponent(id)}/revoke`),
  // POST /auth/invitations/:id/resend (auth-svc) — rota el token en la
  // misma fila y reenvía el correo. `emailSent: false` = la fila se
  // rotó pero el correo no salió; se puede volver a intentar.
  resendInvitation: async (id: string): Promise<ResendInvitationResponse> => {
    const json = await request<unknown>(
      "POST",
      "auth",
      `/auth/invitations/${encodeURIComponent(id)}/resend`
    );
    return resendInvitationResponseSchema.parse(json);
  },
  // GET /organizations/me/member-scopes (core-api) — por usuario, los ids
  // de las tiendas a su cargo y de las zonas que supervisa. Sólo quien
  // tiene algo; roles de lectura de roster o administrador de plataforma.
  memberScopes: async (): Promise<MemberScopesResponse> => {
    const json = await request<unknown>("GET", "core", "/organizations/me/member-scopes");
    return memberScopesResponseSchema.parse(json);
  },
  // PUT /organizations/me/members/:userId/scope (core-api) — el diff
  // {add, remove} sobre las tiendas; `replace: true` traspasa una tienda
  // que ya tiene otro titular (sin él, 409 ASSET_HOLDER_OCCUPIED).
  updateMemberScope: async (
    userId: string,
    body: MemberScopeUpdateBody
  ): Promise<MemberScopeUpdateResponse> => {
    const json = await request<unknown>(
      "PUT",
      "core",
      `/organizations/me/members/${encodeURIComponent(userId)}/scope`,
      { body }
    );
    return memberScopeUpdateResponseSchema.parse(json);
  },
  // GET /organizations/me/invitations/batch/template (core-api) — el
  // .xlsx con los roles que este caller puede otorgar como lista. Binario:
  // requestDownload, porque request() descarta cuerpos que no son JSON;
  // el nombre viene en Content-Disposition (plantilla_invitaciones.xlsx).
  batchTemplate: (): Promise<BlobDownload> =>
    requestDownload("GET", "core", "/organizations/me/invitations/batch/template"),
  // POST /organizations/me/invitations/batch/preview (core-api) —
  // multipart con la planilla en `file`; sin Content-Type, el navegador
  // pone el boundary (lib/api/client.ts, precedente previewWorkbook). No
  // escribe nada: una fila validada por fila con datos. 413
  // FILE_TOO_LARGE, 422 BATCH_FILE_INVALID / BATCH_TOO_LARGE.
  batchPreview: async (file: File): Promise<InvitationBatchPreviewResponse> => {
    const form = new FormData();
    form.set("file", file);
    const json = await request<unknown>(
      "POST",
      "core",
      "/organizations/me/invitations/batch/preview",
      { body: form }
    );
    return invitationBatchPreviewResponseSchema.parse(json);
  },
  // POST /organizations/me/invitations/batch/commit (core-api) — las
  // filas a enviar con el payload que devolvió el preview; el servidor
  // re-valida cada una y responde {sent, failed}, una fila fallida no
  // detiene a las demás. 429 RATE_LIMITED cuando se encadenan lotes.
  batchCommit: async (
    body: InvitationBatchCommitRequest
  ): Promise<InvitationBatchCommitResponse> => {
    const json = await request<unknown>(
      "POST",
      "core",
      "/organizations/me/invitations/batch/commit",
      { body }
    );
    return invitationBatchCommitResponseSchema.parse(json);
  },
};
